import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Divider, Form, Input, Spin } from "antd";
import { LockOutlined, MailOutlined, SafetyOutlined, LoadingOutlined } from "@ant-design/icons";
import aocLogo from "../../assets/images/AOC_LOGOTIP HORITZ.png";

import { FormValues } from "./interfaces";

import { useAuthContext } from "../../providers";

import { emailRules, passwordRules } from "./utils";

import { PATH_ROUTES } from "../../router/routes";
import { initiateValidAuth, processValidCallback } from "../../services/valid";
import { setValidCognitoSession } from "../../services/cognito-valid";

import styles from "./LoginForm.module.css";

const LoginForm = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const { login } = useAuthContext();
  const [searchParams] = useSearchParams();

  const [isInvalidCredentials, setIsInvalidCredentials] = useState(false);
  const [isProcessingValid, setIsProcessingValid] = useState(false);
  const [validError, setValidError] = useState<string | null>(null);

  const onSubmitLogin = async () => {
    try {
      const formValues = await form.validateFields();

      try {
        await login(formValues.email, formValues.password);
        navigate(PATH_ROUTES.home);
      } catch (error) {
        setIsInvalidCredentials(true);
      }
    } catch (error) {
      setIsInvalidCredentials(true);
    }
  };

  // Manejar callback de VALId
  useEffect(() => {
    const handleValidCallback = async () => {
      const code = searchParams.get("code");
      const error = searchParams.get("error");

      // Si hay error de VALId
      if (error) {
        if (error === "SESSION_CANCEL") {
          setValidError("Has cancelado la autenticación con VALId.");
        } else {
          setValidError(`Error en la autenticación: ${error}`);
        }
        return;
      }

      // Si hay código, procesar el callback
      if (code) {
        setIsProcessingValid(true);
        setValidError(null);

        try {
          const result = await processValidCallback(code);
          console.log("VALId authentication successful:", result);
          
          // Si el backend generó tokens de Cognito, establecer sesión automáticamente
          if (result.data.cognito_tokens) {
            try {
              const username = result.data.cognito_data.username;
              await setValidCognitoSession(username, result.data.cognito_tokens);
              console.log("Cognito session established, redirecting to home");
              window.location.href = PATH_ROUTES.home;
              return;
            } catch (sessionError) {
              console.error("Error establishing Cognito session:", sessionError);
              setValidError("Error al establecer la sesión. Por favor, intenta hacer login manualmente.");
              setIsProcessingValid(false);
              return;
            }
          }
          
          // Si no hay tokens de Cognito, mostrar mensaje al usuario
          const { email } = result.data.cognito_data;
          setValidError(
            `Autenticación con VALId exitosa. Usuario: ${email}. ` +
            `Por favor, introduce tu contraseña de Cognito para completar el login, ` +
            `o contacta con el administrador para configurar el auto-login.`
          );
        } catch (err) {
          console.error("Error processing VALId callback:", err);
          const errorMessage = err instanceof Error 
            ? err.message 
            : "Error al procesar la autenticación con VALId. Por favor, inténtalo de nuevo.";
          setValidError(errorMessage);
        } finally {
          setIsProcessingValid(false);
        }
      }
    };

    handleValidCallback();
  }, [searchParams, navigate]);

  const resetInvalidCredentials = () => {
    setIsInvalidCredentials(false);
    setValidError(null);
  };

  const onValidLogin = () => {
    // Iniciar autenticación con VALId
    initiateValidAuth();
  };

  // Si está procesando el callback de VALId, mostrar spinner
  if (isProcessingValid) {
    return (
      <section className={styles.formContainer}>
        <img className={styles.logo} src={aocLogo} alt="Diputacio Logo" />
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <p style={{ marginTop: 20, fontSize: 16 }}>
            Processant autenticació amb VALId...
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.formContainer}>
      <img className={styles.logo} src={aocLogo} alt="Diputacio Logo" />
      <Form className={styles.form} form={form} layout="vertical">
        <header className={styles.formHeader}>
          <h3 className="head3">Plataforma d'anonimització automàtica</h3>
          <h4 className="lg normal">Introdueix les teves credencials per a accedir</h4>
        </header>
        <section className={styles.formContent}>
          <Form.Item
            required={false} // Para eliminar el asterisco
            name="email"
            rules={emailRules}
            label="Correu"
            hasFeedback
            className="customFormItem login"
          >
            <Input
              name="email"
              onChange={resetInvalidCredentials}
              prefix={<MailOutlined />}
              placeholder="Introdueix el teu correu"
            />
          </Form.Item>
          <Form.Item
            required={false} // Para eliminar el asterisco
            name="password"
            rules={passwordRules}
            label="Contrasenya"
            hasFeedback
            className="customFormItem login"
          >
            <Input.Password
              name="password"
              onChange={resetInvalidCredentials}
              prefix={<LockOutlined />}
              placeholder="Introdueix la teva contrasenya"
            />
          </Form.Item>
          {isInvalidCredentials && (
            <Alert
              message={<h4 className="base strong">Error d'inici de sessió</h4>}
              description="L'usuari o la contrasenya no són correctes. Si us plau, torna a intentar-ho."
              type="error"
            />
          )}
          {validError && (
            <Alert
              message={<h4 className="base strong">Error d'autenticació amb VALId</h4>}
              description={validError}
              type="error"
              closable
              onClose={() => setValidError(null)}
            />
          )}
        </section>
        <Button onClick={onSubmitLogin} type="primary" htmlType="submit" block>
          Inicia sessió
        </Button>
        
        <Divider plain style={{ margin: "24px 0", color: "#999" }}>
          o
        </Divider>
        
        <Button 
          onClick={onValidLogin} 
          type="default" 
          icon={<SafetyOutlined />}
          block
          style={{ 
            height: "40px",
            borderColor: "#d9d9d9",
            fontWeight: 500
          }}
        >
          Inicia sessió amb VALId
        </Button>
      </Form>
    </section>
  );
};
export default LoginForm;
